import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RoleSummaryResponse } from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { authorizationApi } from '@/services/authorization.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Spinner } from '@/components/ui';

function roleName(role: RoleSummaryResponse, t: ReturnType<typeof useI18n>['t']): string {
  return role.name ?? t(role.nameKey, { fallback: role.key });
}

/**
 * Every role this tenant can assign: the seeded system roles (read-only —
 * `Role.isSystem` is "cloneable but not editable in place") plus whatever
 * custom roles this tenant has composed from the permission catalog.
 */
export function RolesPage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const canManage = can('authorization.role.create');

  const roles = useQuery({
    queryKey: ['authorization', 'roles'],
    queryFn: () => authorizationApi.listRoles(),
  });

  const archive = useMutation({
    mutationFn: (id: string) => authorizationApi.archiveRole(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['authorization', 'roles'] }),
  });

  const roleList = roles.data ?? [];

  return (
    <div className="page">
      <header className="page-header">
        <h1 className="page-title">{t('administration.roles.title')}</h1>
        {canManage && (
          <Link className="btn" data-variant="primary" to="/administration/roles/new">
            {t('administration.roles.new')}
          </Link>
        )}
      </header>

      {archive.isError && (
        <Alert tone="danger">
          {archive.error instanceof ApiError
            ? t(`errors.${archive.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {roles.isPending && <Spinner label={t('common.states.loading')} />}
      {roles.isSuccess && roleList.length === 0 && (
        <EmptyState title={t('administration.roles.empty')} />
      )}

      {roleList.length > 0 && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('administration.roles.name')}</th>
                <th scope="col">{t('administration.roles.type')}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {roleList.map((role) => (
                <tr key={role.key}>
                  <td>{roleName(role, t)}</td>
                  <td>
                    {role.isSystem
                      ? t('administration.roles.system')
                      : t('administration.roles.custom')}
                  </td>
                  <td>
                    {!role.isSystem && (
                      <>
                        <Link className="btn" to={`/administration/roles/${role.id}`}>
                          {t('common.actions.edit')}
                        </Link>{' '}
                        {canManage && (
                          <Button
                            variant="ghost"
                            loading={archive.isPending && archive.variables === role.id}
                            onClick={() => {
                              if (window.confirm(t('administration.roles.confirmArchive'))) {
                                archive.mutate(role.id);
                              }
                            }}
                          >
                            {t('administration.roles.archive')}
                          </Button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
